<script lang="ts" setup>
import { useMainStore } from "~/stores/main";
import { useQuickViewStore } from "~/stores/quickViewStore";
import QuickViewProductDetails from "~/components/ProductPage/QuickViewProductDetails.vue";
import { useHelpers } from "~/composables/useHelpers";

const mainStore = useMainStore();
const route = useRoute();
const quickViewPanelDiv = ref<HTMLElement | null>(null);
const quickViewStore = useQuickViewStore();

// -----------------------
// reactive properties
// -----------------------

// -----------------------
// methods
// -----------------------
const panelClasses = computed(() => {
  const classes: string[] = [
    "fixed",
    "overflow-auto",
    "quickViewPanel",
    "left-0",
    "z-modal",
    "shadow-xl",
  ];

  if (route.path === "/") {
    classes.push("quickViewPanelHeaderShow");
  } else {
    classes.push("quickViewPanelHeaderOnlyMenu");
  }

  if (mainStore.quickViewVisible) {
    classes.push("quickViewPanelActive");
  }

  return classes.join(" ");
});

// -----------------------
// vue events
// -----------------------
watch(
  () => mainStore.quickViewVisible,
  (isVisible) => {
    if (isVisible) {
      // Clear images before opening to prevent flickering
      quickViewStore.images = [];

      // Add click outside handler with minimal delay
      setTimeout(() => {
        document.addEventListener("click", handleClickOutside);
      }, 50);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }
  },
);

// Watch for product changes to update content when panel is already open
watch(
  () => mainStore.quickViewProduct,
  (newProduct, oldProduct) => {
    if (newProduct && mainStore.quickViewVisible && newProduct !== oldProduct) {
      // Reset the quick view store for the new product
      quickViewStore.reset();

      // Set the new product after a small delay to ensure reactivity
      setTimeout(() => {
        quickViewStore.masterProduct = newProduct as any;

        // Force images to be recalculated by manually setting them
        setTimeout(() => {
          if (
            quickViewStore.masterProduct &&
            quickViewStore.masterProduct.products
          ) {
            // Find the first available variant with images
            const variantWithImages =
              quickViewStore.masterProduct.products.find(
                (variant: any) =>
                  variant.images &&
                  variant.images.length > 0 &&
                  variant.stock > 0,
              );

            if (variantWithImages && variantWithImages.images) {
              const helpers = useHelpers();
              quickViewStore.images = variantWithImages.images.map(
                (src: string) => helpers.getAssetUrl(src),
              );

              // Also set the color if available
              if (variantWithImages.product?.product_szin_property) {
                quickViewStore.color =
                  variantWithImages.product.product_szin_property;
              }
            }
          }
        }, 50);
      }, 10);
    }
  },
  { deep: true },
);

const handleClickOutside = (event: MouseEvent) => {
  // Check if the clicked element is outside myDiv
  if (
    quickViewPanelDiv.value &&
    !quickViewPanelDiv.value.contains(event.target as Node) &&
    mainStore.quickViewVisible
  ) {
    mainStore.quickViewVisible = false;
    mainStore.quickViewProduct = null;
  }
};

const handleClose = () => {
  // Clear images from quick view store to prevent flickering between products
  quickViewStore.images = [];

  mainStore.quickViewVisible = false;
  mainStore.quickViewProduct = null;
};

const handleAddedToCart = () => {
  // Clear images from quick view store to prevent flickering between products
  quickViewStore.images = [];

  // Close panel after adding to cart
  mainStore.quickViewVisible = false;
  mainStore.quickViewProduct = null;
};
</script>

<template>
  <div ref="quickViewPanelDiv" :class="panelClasses">
    <section class="w-100 bg-white overflow-auto p-3 quickViewPanelInner">
      <div class="mb-3 cursor-pointer flex justify-end">
        <i class="m-icon-cancel text-2.5 ml-auto" @click="handleClose"></i>
      </div>
      <QuickViewProductDetails
        v-if="mainStore.quickViewVisible && mainStore.quickViewProduct"
        :product="mainStore.quickViewProduct"
        :use-quick-view-store-prop="true"
        @close="handleClose"
        @added-to-cart="handleAddedToCart"
      />
    </section>
  </div>
</template>
